/**
 * DevView - Development/Testing Utilities
 *
 * Route: /dev
 * Zeigt Debug-Informationen und bietet Aktionen für Testing.
 */

import { signal } from '@preact/signals';
import { db, getDatabaseStats, deleteDatabase, type DatabaseStats } from '../data/db';
import { AlertTriangle, Trash2, RefreshCw, Database, Palette } from 'lucide-preact';
import {
  currentPrimary,
  currentNeutral,
  setPrimary,
  setNeutral,
  AVAILABLE_PRIMARY_COLORS,
  AVAILABLE_NEUTRAL_COLORS,
} from '../stores/theme.store';
import styles from './DevView.module.css';

// =============================================================================
// State
// =============================================================================

const dbStats = signal<DatabaseStats | null>(null);
const dexieVersion = signal<number>(0);
const isLoading = signal(false);
const lastAction = signal<string | null>(null);

// =============================================================================
// Actions
// =============================================================================

async function loadStats(): Promise<void> {
  isLoading.value = true;
  try {
    dbStats.value = await getDatabaseStats();
    dexieVersion.value = db.verno;
  } finally {
    isLoading.value = false;
  }
}

async function nukeDatabase(): Promise<void> {
  if (!confirm('IndexedDB komplett löschen? Schema wird neu erstellt!')) {
    return;
  }

  isLoading.value = true;
  try {
    await deleteDatabase();
    lastAction.value = 'IndexedDB gelöscht - Seite wird neu geladen...';
    setTimeout(() => window.location.reload(), 1000);
  } catch (err) {
    lastAction.value = `Fehler: ${err instanceof Error ? err.message : 'Unbekannt'}`;
    isLoading.value = false;
  }
}

// =============================================================================
// Component
// =============================================================================

export function DevView() {
  // Load stats on mount
  if (dbStats.value === null && !isLoading.value) {
    void loadStats();
  }

  const stats = dbStats.value;

  return (
    <div class={styles.container}>
      <header class={styles.header}>
        <AlertTriangle size={24} />
        <h1>Dev Tools</h1>
      </header>

      {lastAction.value && (
        <div class={styles.actionResult}>
          {lastAction.value}
        </div>
      )}

      {/* Dexie Status */}
      <section class={styles.section}>
        <h2>
          <Database size={18} />
          Dexie (IndexedDB)
        </h2>

        <div class={styles.statGrid}>
          <div class={styles.stat}>
            <span class={styles.statLabel}>Version</span>
            <span class={styles.statValue}>{dexieVersion.value}</span>
          </div>
          <div class={styles.stat}>
            <span class={styles.statLabel}>TimeSheets</span>
            <span class={styles.statValue}>{stats?.timeSheets ?? '-'}</span>
          </div>
          <div class={styles.stat}>
            <span class={styles.statLabel}>TimeEntries</span>
            <span class={styles.statValue}>{stats?.timeEntries ?? '-'}</span>
          </div>
          <div class={styles.stat}>
            <span class={styles.statLabel}>Employees</span>
            <span class={styles.statValue}>{stats?.employees ?? '-'}</span>
          </div>
          <div class={styles.stat}>
            <span class={styles.statLabel}>TenantConfig</span>
            <span class={styles.statValue}>{stats?.hasTenantConfig ? 'Ja' : 'Nein'}</span>
          </div>
          <div class={styles.stat}>
            <span class={styles.statLabel}>PendingOps</span>
            <span class={styles.statValue}>{stats?.pendingOps ?? '-'}</span>
          </div>
        </div>

        <div class={styles.actions}>
          <button
            class={styles.btnRefresh}
            onClick={() => void loadStats()}
            disabled={isLoading.value}
          >
            <RefreshCw size={16} />
            Aktualisieren
          </button>
          <button
            class={styles.btnDanger}
            onClick={() => void nukeDatabase()}
            disabled={isLoading.value}
          >
            <Trash2 size={16} />
            Alles löschen
          </button>
        </div>
      </section>

      {/* Theme Switcher */}
      <section class={styles.section}>
        <h2>
          <Palette size={18} />
          Theme (Dev Only)
        </h2>
        <p class={styles.hint}>
          Später via TenantConfig vom Backend.
        </p>

        {/* Primary Color */}
        <h3 class={styles.colorGroupTitle}>Primary Color</h3>
        <div class={styles.themeGrid}>
          {AVAILABLE_PRIMARY_COLORS.map((color) => (
            <button
              key={color}
              class={`${styles.themeBtn} ${currentPrimary.value === color ? styles.themeBtnActive : ''}`}
              onClick={() => setPrimary(color)}
            >
              <span class={styles.themeColor} data-color={color} />
              <span class={styles.themeLabel}>{color}</span>
            </button>
          ))}
        </div>

        {/* Neutral Color */}
        <h3 class={styles.colorGroupTitle}>Neutral Color</h3>
        <div class={styles.themeGrid}>
          {AVAILABLE_NEUTRAL_COLORS.map((color) => (
            <button
              key={color}
              class={`${styles.themeBtn} ${currentNeutral.value === color ? styles.themeBtnActive : ''}`}
              onClick={() => setNeutral(color)}
            >
              <span class={styles.neutralColor} data-neutral={color} />
              <span class={styles.themeLabel}>{color}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Back Link */}
      <a href="/" class={styles.backLink}>
        Zurück zur App
      </a>
    </div>
  );
}
